/**
 * <uhs-purchase-bar> — publishes the PDP purchase row's height as
 * --uhs-pdp-bar-h on :root.
 *
 * Below 750px assets/component-uhs-buy-box.css pins this element to the
 * bottom of the viewport (Figma 2419:6811), which takes it out of flow and
 * leaves nothing reserving space for it — the end of the page would sit
 * underneath. That stylesheet pads the body by --uhs-pdp-bar-h to compensate;
 * this element is where the number comes from.
 *
 * Measured, not hardcoded. The bar is 117px in the Figma frame, but Dawn's
 * <pickup-availability> lives inside this row and only fills itself in after
 * an async fetch, so the real height can grow after first paint on any store
 * with local pickup enabled. A ResizeObserver keeps the padding honest
 * instead of assuming the design's number.
 *
 * Same approach as --uhs-header-h (sections/uhs-header.liquid),
 * --uhs-ymm-h (sections/uhs-ymm-finder.liquid) and --uhs-bottom-bar-h
 * (sections/uhs-mobile-bottom-bar.liquid), which the bar's own `bottom`
 * offset reads.
 *
 * The variable is published unconditionally, at every viewport. Only the
 * mobile block consumes it, so there is no need to duplicate the breakpoint
 * here in JS where it could drift from the stylesheet.
 *
 * Teardown matters: on a combined-listing product swap Dawn's
 * assets/product-info.js replaces the whole <product-info> element, so this
 * element is disconnected and a fresh one connects. The observer is
 * disconnected and the custom property removed here so a stale bar height can
 * never outlive the element that measured it.
 */
if (!customElements.get('uhs-purchase-bar')) {
  customElements.define(
    'uhs-purchase-bar',
    class UhsPurchaseBar extends HTMLElement {
      connectedCallback() {
        this.setHeightVar = () => {
          document.documentElement.style.setProperty('--uhs-pdp-bar-h', `${this.offsetHeight}px`);
        };

        this.setHeightVar();

        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.setHeightVar);
          this.resizeObserver.observe(this);
        } else {
          window.addEventListener('resize', this.setHeightVar);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else if (this.setHeightVar) {
          window.removeEventListener('resize', this.setHeightVar);
        }

        document.documentElement.style.removeProperty('--uhs-pdp-bar-h');
      }
    },
  );
}
